import { z } from 'zod';
import { optionalAgentIdSchema } from './agent-tool-schemas';
// How an agent's versions reach live traffic. Saving a draft changes nothing live. Publishing makes the draft the
// latest published version, which everything that follows the agent uses from its next call or message: phone numbers
// without a label, campaigns (they can't be pinned), email and HubSpot channels, and handoffs. A phone number pinned
// to a label keeps that label's version until someone points the label at another version.
/** How many published versions get_agent_deployment lists, newest first. */
export const DEPLOYMENT_VERSIONS_SHOWN = 5;
/** Label names Via creates: short and URL-safe, like v1-address-change. */
export const VERSION_LABEL_NAME_PATTERN = /^[a-z0-9][a-z0-9._-]{0,39}$/;
const liveTargetSchema = z.object({
    kind: z.enum(['phone number', 'campaign', 'email channel', 'HubSpot inbox']),
    name: z.string(),
    follows: z.string().describe('The latest published version, or the label it is pinned to')
});
export const getAgentDeploymentToolSchema = {
    name: 'get_agent_deployment',
    description: "Check where an agent is live before publishing: whether it has unpublished changes, its recent published versions and labels, what switches to a new version when it's published, and what stays on a label.",
    inputSchema: z.object({ agentId: optionalAgentIdSchema }),
    outputSchema: z.object({
        agentId: z.string(),
        unpublishedChanges: z.boolean().describe('The saved draft differs from the latest published version'),
        versions: z.array(z.object({ versionId: z.string(), publishedAt: z.string(), labels: z.array(z.string()) })),
        labels: z.array(z.object({
            labelId: z.string(),
            name: z.string(),
            versionId: z.string().optional().describe("Missing when it isn't pinned to a version")
        })),
        switchesOnPublish: z
            .array(liveTargetSchema)
            .describe('Live now, and uses a newly published version from its next call or message'),
        staysOnLabel: z.array(liveTargetSchema).describe("Pinned to a label, so publishing doesn't change it")
    })
};
export const createVersionLabelToolSchema = {
    name: 'create_version_label',
    description: 'Name a published version with a label, so phone numbers can be pinned to it on their page. It changes no live calls: nothing uses a new label until a number is pinned to it.',
    inputSchema: z.object({
        agentId: optionalAgentIdSchema,
        name: z
            .string()
            .regex(VERSION_LABEL_NAME_PATTERN, 'Lowercase letters, numbers, dots, hyphens and underscores, up to 40')
            .describe('Describes the milestone, such as v1-address-change. The user can edit it before it is created.'),
        versionId: z
            .string()
            .optional()
            .describe('A published version from get_agent_deployment. Defaults to the latest published version.')
    }),
    outputSchema: z.object({ labelId: z.string(), name: z.string(), versionId: z.string() })
};
const LATEST = 'the latest published version';
export const describeAgentDeployment = ({ agentId, draft, versions, labels, phoneNumbers, campaigns, emailChannels, hubspotInboxes }) => {
    const labelById = new Map(labels.map((label) => [label.labelId, label]));
    const numberTargets = phoneNumbers
        .filter(({ assistantId }) => assistantId === agentId)
        .map(({ name, number, labelId }) => {
        const label = labelId ? labelById.get(labelId) : undefined;
        const displayName = [name, number].filter(Boolean).join(' ') || 'A phone number';
        if (!label)
            return {
                pinned: false,
                target: { kind: 'phone number', name: displayName, follows: LATEST }
            };
        // An inbound call on a number whose label points nowhere quietly falls back to the latest published version.
        if (!label.versionId) {
            return {
                pinned: false,
                target: {
                    kind: 'phone number',
                    name: displayName,
                    follows: `${LATEST} (its label "${label.name}" isn't pinned to a version)`
                }
            };
        }
        return {
            pinned: true,
            target: { kind: 'phone number', name: displayName, follows: `label "${label.name}"` }
        };
    });
    const followsLatest = [
        ...numberTargets.filter(({ pinned }) => !pinned).map(({ target }) => target),
        ...campaigns
            .filter(({ assistantId, status, archived }) => assistantId === agentId && !archived && status !== 'Completed')
            .map(({ campaignName, status }) => ({
            kind: 'campaign',
            name: `${campaignName} (${status})`,
            follows: LATEST
        })),
        ...emailChannels
            .filter(({ vapiAssistantId }) => vapiAssistantId === agentId)
            .map(({ emailAddress }) => ({ kind: 'email channel', name: emailAddress, follows: LATEST })),
        ...hubspotInboxes
            .filter(({ vapiAssistantId }) => vapiAssistantId === agentId)
            .map(({ hubspotAccountId }) => ({
            kind: 'HubSpot inbox',
            name: `HubSpot account ${hubspotAccountId}`,
            follows: LATEST
        }))
    ];
    return {
        agentId,
        unpublishedChanges: !draft.deployedAt,
        versions: versions.slice(0, DEPLOYMENT_VERSIONS_SHOWN).map(({ versionId, deployedAt }) => ({
            versionId,
            publishedAt: new Date(deployedAt).toISOString(),
            labels: labels.filter((label) => label.versionId === versionId).map(({ name }) => name)
        })),
        labels: labels.map(({ labelId, name, versionId }) => ({
            labelId,
            name,
            ...(versionId && { versionId })
        })),
        switchesOnPublish: followsLatest,
        staysOnLabel: numberTargets.filter(({ pinned }) => pinned).map(({ target }) => target)
    };
};
//# sourceMappingURL=deployment.js.map